import { Component } from '@theme/component';

/**
 * Sliding panel stack for the mobile menu drawer.
 *
 * Panels are flat sibling elements linked by `data-panel-id` /
 * `data-parent-panel`. Drill buttons reference their target panel with
 * `data-target-panel`. Only the active panel is interactive; all other
 * panels carry the `inert` attribute so the drawer's focus trap skips them.
 *
 * @extends {Component}
 */
class GeekzPanelMenu extends Component {
  /** @type {HTMLDetailsElement | null} */
  #drawerDetails = null;

  connectedCallback() {
    super.connectedCallback();
    this.#drawerDetails = this.closest('details');
    this.#drawerDetails?.addEventListener('toggle', this.#onDrawerToggle);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#drawerDetails?.removeEventListener('toggle', this.#onDrawerToggle);
  }

  get activePanelId() {
    return this.dataset.activePanel ?? 'root';
  }

  /**
   * @param {string} id
   * @returns {HTMLElement | null}
   */
  #panel(id) {
    return this.querySelector(`[data-panel-id="${id}"]`);
  }

  /**
   * Open the panel referenced by the clicked drill button.
   * @param {Event} event
   */
  drill(event) {
    if (!(event.target instanceof Element)) return;

    const button = event.target.closest('[data-target-panel]');
    const targetId = button?.getAttribute('data-target-panel');
    if (!button || !targetId) return;

    button.setAttribute('aria-expanded', 'true');
    this.#activate(targetId);

    const next = this.#panel(targetId);
    const focusTarget = next?.querySelector('.geekz-panel__back');
    if (focusTarget instanceof HTMLElement) focusTarget.focus({ preventScroll: true });
  }

  /** Return to the parent of the active panel. */
  back() {
    const closingId = this.activePanelId;
    const parentId = this.#panel(closingId)?.getAttribute('data-parent-panel');
    if (!parentId) return;

    const drillButton = this.querySelector(`[data-target-panel="${closingId}"]`);
    drillButton?.setAttribute('aria-expanded', 'false');

    this.#activate(parentId);

    if (drillButton instanceof HTMLElement) drillButton.focus({ preventScroll: true });
  }

  /** Reset to the root panel instantly when the drawer closes. */
  #onDrawerToggle = () => {
    if (this.#drawerDetails?.open) return;

    this.classList.add('geekz-panels--no-animation');
    this.#activate('root');

    for (const button of this.querySelectorAll('[data-target-panel][aria-expanded="true"]')) {
      button.setAttribute('aria-expanded', 'false');
    }

    void this.offsetWidth;
    this.classList.remove('geekz-panels--no-animation');
  };

  /**
   * Make a panel active, mark its ancestor chain as "behind", park the rest.
   * @param {string} id
   */
  #activate(id) {
    const next = this.#panel(id);
    if (!next) return;

    this.dataset.activePanel = id;

    for (const panel of this.querySelectorAll('[data-panel-id]')) {
      panel.classList.remove('geekz-panel--active', 'geekz-panel--behind');
      panel.setAttribute('inert', '');
    }

    next.classList.add('geekz-panel--active');
    next.removeAttribute('inert');
    next.scrollTop = 0;

    let parentId = next.getAttribute('data-parent-panel');
    while (parentId) {
      const parent = this.#panel(parentId);
      if (!parent) break;
      parent.classList.add('geekz-panel--behind');
      parentId = parent.getAttribute('data-parent-panel');
    }
  }
}

if (!customElements.get('geekz-panel-menu')) {
  customElements.define('geekz-panel-menu', GeekzPanelMenu);
}
